import { skipToken, useSuspenseQuery } from "@apollo/client/react";
import { useMemo } from "react";

import {
  GetCategoryDetailDocument,
  type GetCategoryDetailQuery,
  type GetCategoryDetailQueryVariables,
  ListEffectiveCategoryPropertySchemasDocument,
  type ListEffectiveCategoryPropertySchemasQuery,
  type ListEffectiveCategoryPropertySchemasQueryVariables,
} from "~/features/admin/categories/gqls";
import { useTenant } from "~/features/tenant/context";

/**
 * Category detail with relation types and audit timestamps, plus the
 * category's EFFECTIVE property schemas (own + inherited from ancestors,
 * read-time resolved by `cnutListEffectiveCategoryPropertySchemas`).
 *
 * `categories` scoping is enforced server-side via the Hasura session, so no
 * tenantId variable is needed there — but the effective-schemas query is a
 * Cnut resolver (not a Hasura table), which requires tenantId explicitly.
 */
export function useCategoryDetail(categoryId: string) {
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");

  const detail = useSuspenseQuery<GetCategoryDetailQuery, GetCategoryDetailQueryVariables>(GetCategoryDetailDocument, {
    variables: { categoryId },
  });

  // For an absent category the resolver's findByID surfaces not-found as an error,
  // which would reject the suspense query instead of yielding the caller's empty state.
  const categoryExists = detail.data.categories.length > 0;
  const effectiveSchemasArg = useMemo(
    () =>
      categoryExists
        ? { variables: { tenantId, categoryId } satisfies ListEffectiveCategoryPropertySchemasQueryVariables }
        : skipToken,
    [categoryExists, tenantId, categoryId],
  );
  const effectiveSchemas = useSuspenseQuery<
    ListEffectiveCategoryPropertySchemasQuery,
    ListEffectiveCategoryPropertySchemasQueryVariables
  >(ListEffectiveCategoryPropertySchemasDocument, effectiveSchemasArg);

  return { data: detail.data, effectiveSchemasData: effectiveSchemas.data };
}
